import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FaBars, FaPaperclip, FaPaperPlane, FaSmile, FaCopy, FaTrash, FaEllipsisH, FaSearch, FaArrowLeft, FaInfoCircle, FaComments, FaClock, FaCheck, FaHourglassHalf, FaExclamationTriangle, FaMapMarkerAlt, FaGlobe, FaClipboardList, FaImage, FaFileAlt, FaLock, FaStar, FaUser, FaChalkboardTeacher, FaUserGraduate, FaTimesCircle, FaChartBar, FaWifi, FaExclamationCircle } from 'react-icons/fa';
import { fetchConversations, fetchMessages, sendMessage, subscribeToMessages, unsubscribeFromMessages, onConnectionChange, getConnectionState, processMessageQueue } from './chatService';
import { supabase } from './supabaseClient';
import './Chat.css';
import LoadingSpinner from './components/LoadingSpinner';
import { batchGetUserDetails } from './services/userBatchService';
import logger from './utils/logger';

// Function to get full profile data
async function getFullProfile(userId) {
  try {
    // Try to get from students table first
    let { data: studentData, error: studentError } = await supabase
      .from('students')
      .select('*')
      .eq('uuid_id', userId)
      .single();

    if (studentData && !studentError) {
      return { ...studentData, role: 'student' };
    }

    // If not found in students, try teachers table
    let { data: teacherData, error: teacherError } = await supabase
      .from('Teachers')
      .select('*')
      .eq('uuid_id', userId)
      .single();

    if (teacherData && !teacherError) {
      return { ...teacherData, role: 'teacher' };
    }

    return null;
  } catch (error) {
    logger.error('Error fetching full profile:', error);
    return null;
  }
}

// Chat state
function useSupabaseChat(onMessageSent) {
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [userDetails, setUserDetails] = useState({}); // Store user details
  const [lastMessages, setLastMessages] = useState({}); // Store last message per conversation
  const [isOnline, setIsOnline] = useState(getConnectionState()); // Connection state
  const [showOfflineBanner, setShowOfflineBanner] = useState(false); // Show offline banner

  // Get user on mount
  useEffect(() => {
    async function getUser() {
      const { data: { user }, error } = await supabase.auth.getUser();
      logger.debug('CHAT DEBUG: getUser result:', { user, error });
      setUser(user);
    }
    getUser();
  }, []);

  // Fetch conversations when user loads - OPTIMIZED with batch queries
  useEffect(() => {
    if (!user) return;

    async function loadConversations() {
      try {
        setLoading(true);

        // 1. Fetch conversations (1 query)
        const { data, error } = await fetchConversations(user.id);

        if (data && data.length > 0) {
          // 2. Extract all unique user IDs (no query, just processing)
          const userIds = new Set();
          data.forEach(conv => {
            const otherUserId = user.id === conv.user1_id ? conv.user2_id : conv.user1_id;
            userIds.add(otherUserId);
          });

          const userIdsArray = Array.from(userIds);

          // 3. Batch fetch all user details (2 queries: students + teachers)
          const details = await batchGetUserDetails(userIdsArray);
          setUserDetails(details);

          // 4. Batch fetch last messages for all conversations (1 query)
          const conversationIds = data.map(c => c.id);
          const { data: allMessages } = await supabase
            .from('messages')
            .select('conversation_id, content, created_at')
            .in('conversation_id', conversationIds)
            .order('created_at', { ascending: false });

          // 5. Build last messages map (take first/latest message per conversation)
          const lastMsgs = {};
          if (allMessages) {
            allMessages.forEach(msg => {
              if (!lastMsgs[msg.conversation_id]) {
                lastMsgs[msg.conversation_id] = msg;
              }
            });
          }

          // Set null for conversations without messages
          data.forEach(conv => {
            if (!lastMsgs[conv.id]) {
              lastMsgs[conv.id] = null;
            }
          });

          setLastMessages(lastMsgs);
        }

        setConversations(data || []);
        setActiveConv(data && data.length > 0 ? data[0] : null);
      } catch (error) {
        logger.error('Error loading conversations:', error);
      } finally {
        setLoading(false);
      }
    }

    loadConversations();
  }, [user]);

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (!activeConv) return;
    setMsgLoading(true);
    fetchMessages(activeConv.id).then(({ data, error }) => {
      setMessages(data || []);
      setMsgLoading(false);
    });
  }, [activeConv]);

    // Subscribe to new messages
  useEffect(() => {
    if (!activeConv || !user) return;
    
    logger.debug('Setting up real-time subscription for conversation:', activeConv.id);
    
    const channel = subscribeToMessages(activeConv.id, (payload) => {
      logger.debug('Real-time message received:', payload);
      
      if (payload.eventType === 'INSERT' && payload.new) {
        setMessages(prev => {
          // Check if this is replacing a temporary message
          const tempMessageIndex = prev.findIndex(msg => 
            msg.id.startsWith('temp-') && 
            msg.sender_id === payload.new.sender_id && 
            msg.content === payload.new.content
          );
          
          if (tempMessageIndex !== -1) {
            // Replace temporary message with real message
            logger.debug('Replacing temporary message with real message:', payload.new);
            const newMessages = [...prev];
            newMessages[tempMessageIndex] = payload.new;
            return newMessages;
          }
          
          // Check if message already exists to prevent duplicates
          const exists = prev.some(msg => msg.id === payload.new.id);
          if (exists) {
            logger.debug('Message already exists, skipping duplicate');
            return prev;
          }
          
          logger.debug('Adding new message to chat:', payload.new);
          return [...prev, payload.new];
        });
      }
    });
    
    setSub(channel);
    
    return () => {
      logger.debug('Cleaning up real-time subscription');
      if (channel) unsubscribeFromMessages(channel);
    };
  }, [activeConv, user]);

  // Monitor connection state and process queue when back online
  useEffect(() => {
    const unsubscribe = onConnectionChange((online) => {
      logger.info('Connection state changed:', online ? 'online' : 'offline');
      setIsOnline(online);

      if (!online) {
        // Show offline banner after a short delay to avoid flashing
        const timer = setTimeout(() => setShowOfflineBanner(true), 500);
        return () => clearTimeout(timer);
      } else {
        // Hide banner immediately when back online
        setShowOfflineBanner(false);

        // Process any queued messages
        processMessageQueue().then(() => {
          logger.debug('✅ Message queue processed');
        }).catch(error => {
          logger.error('❌ Error processing message queue:', error);
        });
      }
    });

    return unsubscribe;
  }, []);

  const sendMessageHandler = async (content, attachmentUrl = null, attachmentName = null) => {
    if (!activeConv || !user) return;
    
    const receiverId = user.id === activeConv.user1_id ? activeConv.user2_id : activeConv.user1_id;
    
    // Create a temporary message for immediate UI update (like location messages)
    const tempMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: activeConv.id,
      sender_id: user.id,
      content,
      attachment_url: attachmentUrl,
      attachment_name: attachmentName,
      created_at: new Date().toISOString(),
      status: 'sending'
    };
    
    // Add message to local state immediately
    setMessages(prev => [...prev, tempMessage]);
    
    // Trigger scroll callback if provided
    if (onMessageSent) {
      setTimeout(onMessageSent, 50);
    }

    const { data, error, queued } = await sendMessage({
      conversationId: activeConv.id,
      senderId: user.id,
      receiverId,
      content,
      attachmentUrl,
      attachmentName
    });

    if (queued) {
      // Message was queued for later delivery
      logger.debug('📤 Message queued for delivery when online');
      // Update the temp message to show queued status
      setMessages(prev => prev.map(msg =>
        msg.id === tempMessage.id
          ? { ...msg, id: data.id, status: 'queued' }
          : msg
      ));
    } else if (error) {
      logger.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
      // Remove the temporary message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    } else {
      logger.debug('Message sent successfully:', data);
      // Replace temporary message with real message when it comes through real-time
      // The real-time subscription will handle this
    }
  };

  return {
    user,
    conversations,
    activeConv,
    setActiveConv,
    messages,
    setMessages,
    loading,
    msgLoading,
    userDetails,
    sendMessage: sendMessageHandler,
    lastMessages,
    isOnline,
    showOfflineBanner,
  };
}

// Helper to group messages by date
function groupMessagesByDate(messages) {
  const groups = {};
  messages.forEach(msg => {
    // For mock: use id to simulate date, real impl would use msg.date
    let dateLabel;
    if (msg.id <= 2) dateLabel = 'Today';
    else dateLabel = 'Yesterday';
    if (!groups[dateLabel]) groups[dateLabel] = [];
    groups[dateLabel].push(msg);
  });
  return groups;
}

const bubbleFadeIn = {
  animation: 'bubbleFadeIn 0.5s',
};

const bubbleHover = {
  filter: 'brightness(0.98)',
  boxShadow: '0 2px 12px #e0eaff',
};

// Add keyframes for fade-in
const styleSheet = document.createElement('style');
styleSheet.textContent = `
@keyframes bubbleFadeIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
}`;

// Add CSS animations for typing indicator and pulse
const animationStyles = `
  @keyframes typingDot {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const animationStyleSheet = document.createElement('style');
animationStyleSheet.textContent = animationStyles;
document.head.appendChild(animationStyleSheet);

document.head.appendChild(styleSheet);

const ChatPage = () => {
  const { isArabic, sidebarVisible, setSidebarVisible, handleLanguageChange, cachedUserProfile } = useOutletContext() || {};
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [input, setInput] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null); // Add file input ref
  const [showEmoji, setShowEmoji] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState({}); // Track who is typing in each conversation
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // Track upload progress per file
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [customLocationName, setCustomLocationName] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [accuracyReadings, setAccuracyReadings] = useState([]);
  const [favoriteLocations, setFavoriteLocations] = useState([]);
  const [locationHistory, setLocationHistory] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({}); // Track unread counts per conversation
  const [messageStatuses, setMessageStatuses] = useState({}); // Track message status (sending, sent, delivered, read)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 700;
  const messagesEndRef = useRef(null);
  const [mobileView, setMobileView] = useState('conversations');
  const [mobileSelectedFiles, setMobileSelectedFiles] = useState([]);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const typingTimeoutRef = useRef({});
  const inputTimeoutRef = useRef(null); // Image to preview
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const {
    user,
    conversations,
    activeConv,
    setActiveConv,
    messages,
    setMessages,
    loading,
    msgLoading,
    userDetails,
    sendMessage,
    lastMessages,
    isOnline,
    showOfflineBanner,
  } = useSupabaseChat(scrollToBottom);

  // Auto-scroll to bottom when messages change or when opening a conversation
  useEffect(() => {
    if (messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, activeConv]);
  
  // Mark conversation as read when active conversation changes
  useEffect(() => {
    if (activeConv?.id) {
      setUnreadCounts(prev => ({ ...prev, [activeConv.id]: 0 }));
    }
  }, [activeConv]);

  // Handle window resize for responsive margins
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock screen scrolling on mobile
  useEffect(() => {
    if (isMobile) {
      // Save original values
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      const originalBodyHeight = document.body.style.height;
      const originalHtmlHeight = document.documentElement.style.height;

      // Lock scrolling on both body and html
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      document.documentElement.style.height = '100vh';

      // Restore on unmount
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        document.body.style.height = originalBodyHeight;
        document.documentElement.style.height = originalHtmlHeight;
      };
    }
  }, [isMobile]);

  // Update message statuses when messages change
  useEffect(() => {
    if (!messages || !user) return;
    
    const newStatuses = {};
    messages.forEach(msg => {
      if (msg.sender_id === user.id) {
        // For sent messages, check if they've been read
        // This is a simplified version - in production, you'd check read receipts from the database
        const messageAge = Date.now() - new Date(msg.created_at).getTime();
        if (msg.status === 'sending') {
          newStatuses[msg.id] = 'sent';
        } else if (messageAge > 5000 && !newStatuses[msg.id]) {
          // Simulate delivered status after 5 seconds
          newStatuses[msg.id] = 'delivered';
        }
      }
    });
    
    if (Object.keys(newStatuses).length > 0) {
      setMessageStatuses(prev => ({ ...prev, ...newStatuses }));
    }
  }, [messages, user]);

  // Mobile navigation functions
  const handleMobileConversationSelect = (conversation) => {
    setActiveConv(conversation);
    setMobileView('chat');
  };

  const handleMobileBackToConversations = () => {
    setMobileView('conversations');
  };

  // Handle opening profile modal
  const handleOpenProfile = async (userId) => {
    if (!userId) return;
    
    setLoadingProfile(true);
    setShowProfileModal(true);
    
    try {
      const profile = await getFullProfile(userId);
      setProfileData(profile);
    } catch (error) {
      logger.error('Error loading profile:', error);
      setProfileData(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Reset mobile view to conversations when conversations change
  useEffect(() => {
    if (isMobile && conversations.length > 0 && !activeConv) {
      setActiveConv(conversations[0]);
    }
  }, [conversations, isMobile, activeConv]);
  // Load saved location data on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('locationFavorites');
    const savedHistory = localStorage.getItem('locationHistory');
    
    if (savedFavorites) {
      setFavoriteLocations(JSON.parse(savedFavorites));
    }
    if (savedHistory) {
      setLocationHistory(JSON.parse(savedHistory));
    }
  }, []);



  // Handle file input change - add files to selected files instead of immediate upload
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Add files to selected files state
      setSelectedFiles(prev => [...prev, ...files]);
    }
    // Reset input
    e.target.value = '';
  };

  // Handle drag and drop - add files to selected files
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  // Remove selected file
  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Detect contact information in messages
  const detectContactInfo = (text) => {
    const patterns = {
      email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      phone: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}|\d{8,}/g,
      whatsapp: /(whatsapp|wa\.me|wa\.com)/gi,
      instagram: /(@[\w.]+|instagram\.com\/[\w.]+)/gi,
      snapchat: /(snapchat|snap\.chat)/gi,
      telegram: /(telegram|t\.me)/gi,
      social: /(facebook|twitter|linkedin|discord)/gi
    };
    
    const detected = [];
    for (const [type, pattern] of Object.entries(patterns)) {
      if (pattern.test(text)) {
        detected.push(type);
      }
    }
    
    return detected;
  };

  // Send message with selected files
  const handleSendMessage = async () => {
    if (!input.trim() && selectedFiles.length === 0) return;
    
    // Check for contact information in message
    const contactInfoDetected = detectContactInfo(input.trim());
    if (contactInfoDetected.length > 0) {
      const contactTypes = {
        email: isArabic ? 'البريد الإلكتروني' : 'email addresses',
        phone: isArabic ? 'أرقام الهواتف' : 'phone numbers',
        whatsapp: 'WhatsApp',
        instagram: 'Instagram',
        snapchat: 'Snapchat',
        telegram: 'Telegram',
        social: isArabic ? 'روابط وسائل التواصل الاجتماعي' : 'social media links'
      };
      
      const detectedTypes = contactInfoDetected.map(type => contactTypes[type] || type).join(', ');
      
      const confirmMessage = isArabic
        ? `⚠️ تحذير: يبدو أن رسالتك تحتوي على معلومات اتصال (${detectedTypes}).\n\nجميع الحجوزات يجب أن تتم عبر المنصة للحصول على الحماية والميزات الحصرية.\n\nهل تريد المتابعة؟`
        : `⚠️ Warning: Your message appears to contain contact information (${detectedTypes}).\n\nAll bookings must be made through the platform to receive protection and exclusive features.\n\nDo you want to continue?`;
      
      if (!window.confirm(confirmMessage)) {
        return; // User cancelled
      }
      
      // Show additional warning
      alert(isArabic 
        ? 'تذكر: الحجوزات خارج المنصة لا تتمتع بالحماية ولا تشمل الميزات الحصرية مثل التسجيلات وملخصات الذكاء الاصطناعي.'
        : 'Remember: Off-platform bookings do not include protection or exclusive features like recordings and AI summaries.');
    }
    
    // If there are selected files, upload them first
    if (selectedFiles.length > 0) {
      setUploading(true);
      try {
        for (const file of selectedFiles) {
          // Initialize progress
          setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
          
          // Simulate progress updates (in production, use actual upload progress)
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
              const current = prev[file.name] || 0;
              if (current < 90) {
                return { ...prev, [file.name]: current + 10 };
              }
              return prev;
            });
          }, 200);
          // Check file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            alert('File too large. Maximum size is 10MB.');
            continue;
          }

          // Check file type
          const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
          if (!allowedTypes.includes(file.type)) {
            alert('File type not supported. Please upload images, PDFs, or text files.');
            continue;
          }

          // Generate unique filename
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `chat-attachments/${activeConv.id}/${fileName}`;

          // Upload to Supabase Storage
          const { data, error } = await supabase.storage
            .from('chat-files')
            .upload(filePath, file);

          if (error) {
            logger.error('Upload error:', error);
            alert('Failed to upload file. Please try again.');
            continue;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('chat-files')
            .getPublicUrl(filePath);
          
          // Complete progress
          clearInterval(progressInterval);
          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));

          // Send message with both text and attachment
          // If no text, use file name as message content for backend validation
          const messageText = input.trim() || `📎 ${file.name}`;
          await sendMessage(messageText, publicUrl, file.name); // Pass file name separately
        }
        // Clear selected files and progress after successful upload
        setSelectedFiles([]);
        setUploadProgress({});
        setInput('');
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
      } catch (error) {
        logger.error('File upload error:', error);
        alert('Failed to upload file. Please try again.');
        // Clear progress on error
        setUploadProgress({});
      } finally {
        setUploading(false);
      }
    } else {
      // Send text message only
      await sendMessage(input.trim());
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  const handleShareLocation = async () => {
    setShowLocationModal(true);
    setIsLoadingLocation(true);
    setLocationError(null);
    setCurrentLocation(null);
    setAccuracyReadings([]);

    // Debug: Check if geolocation is available
    logger.debug('Geolocation available:', !!navigator.geolocation);
    logger.debug('HTTPS:', window.location.protocol === 'https:');
    logger.debug('Localhost:', window.location.hostname === 'localhost');

    if (navigator.geolocation) {
      // Function to get multiple readings for better accuracy
      const getMultipleReadings = (count = 3) => {
        const readings = [];
        let completed = 0;

        const getReading = (index) => {
          const options = {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 30000 // 30 seconds for fresher data
          };

          navigator.geolocation.getCurrentPosition(
            (position) => {
              readings[index] = position.coords;
              completed++;
              logger.debug(`Reading ${index + 1} completed:`, position.coords);
              
              if (completed === count) {
                // Calculate average coordinates and best accuracy
                const avgLat = readings.reduce((sum, reading) => sum + reading.latitude, 0) / count;
                const avgLng = readings.reduce((sum, reading) => sum + reading.longitude, 0) / count;
                const bestAccuracy = Math.min(...readings.map(r => r.accuracy));
                
                const finalLocation = {
                  latitude: avgLat,
                  longitude: avgLng,
                  accuracy: bestAccuracy,
                  readings: readings.length
                };
                
                logger.debug('Final averaged location:', finalLocation);
                setCurrentLocation(finalLocation);
                setAccuracyReadings(readings);
                setIsLoadingLocation(false);
              }
            },
            (error) => {
              logger.error(`Reading ${index + 1} failed:`, error);
              completed++;
              
              if (completed === count) {
                // If all readings failed, try single reading with low accuracy
                logger.warn('All readings failed, trying single low accuracy reading...');
                getSingleReading();
              }
            },
            options
          );
        };

        // Start multiple readings
        for (let i = 0; i < count; i++) {
          setTimeout(() => getReading(i), i * 1000); // Stagger readings by 1 second
        }
      };

      // Fallback to single reading
      const getSingleReading = () => {
        const options = {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 60000
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            logger.debug('Single GPS Success:', position.coords);
            const { latitude, longitude, accuracy } = position.coords;
            setCurrentLocation({ latitude, longitude, accuracy, readings: 1 });
            setIsLoadingLocation(false);
          },
          (error) => {
            logger.error('Geolocation error:', error);
            logger.error('Error code:', error.code);
            logger.error('Error message:', error.message);
            
            // If high accuracy failed, try with low accuracy
            if (error.code === 2 && options.enableHighAccuracy) {
              logger.debug('Trying with low accuracy...');
              options.enableHighAccuracy = false;
              options.timeout = 15000;
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  logger.debug('GPS Success (low accuracy):', position.coords);
                  const { latitude, longitude, accuracy } = position.coords;
                  setCurrentLocation({ latitude, longitude, accuracy, readings: 1 });
                  setIsLoadingLocation(false);
                },
                (retryError) => {
                  logger.error('Retry failed:', retryError);
                  setLocationError(retryError);
                  setIsLoadingLocation(false);
                },
                options
              );
            } else {
              setLocationError(error);
              setIsLoadingLocation(false);
            }
          },
          options
        );
      };

      // Start with multiple readings for better accuracy
      getMultipleReadings(3);
    } else {
      setLocationError({ code: 0, message: 'Geolocation is not supported by this browser.' });
      setIsLoadingLocation(false);
    }
  };

  const handleSendCurrentLocation = () => {
    if (currentLocation) {
      const locationType = detectLocationType(new Date());
      const locationName = "My Current Location";
      
      shareLocation(
        currentLocation.latitude, 
        currentLocation.longitude, 
        locationName, 
        locationType
      );
      
      // Ask user if they want to save this location
      const saveToFavorites = window.confirm(
        `Would you like to save "${locationName}" to your favorites?`
      );
      
      if (saveToFavorites) {
        const customName = window.prompt("Enter a name for this location (e.g., Home, Office):", "Current Location");
        if (customName && customName.trim()) {
          saveFavoriteLocation(customName.trim(), currentLocation.latitude, currentLocation.longitude, locationType);
        }
      }
    }
  };

  const handleSendCustomLocation = () => {
    if (customLocationName.trim()) {
      const locationType = detectLocationType(new Date());
      const locationName = customLocationName.trim();
      
      // For custom locations, we'll use a search URL
      const encodedLocation = encodeURIComponent(locationName);
      const mapsUrl = `https://www.google.com/maps/search/${encodedLocation}`;
      const locationMessage = `${locationType} ${locationName}\n🌐 [View on Maps](${mapsUrl})`;
      
      sendMessage(locationMessage);
      addToHistory(0, 0, locationName); // Add to history with placeholder coordinates
      setShowLocationModal(false);
      setCurrentLocation(null);
      setCustomLocationName('');
      
      // Ask user if they want to save this location
      const saveToFavorites = window.confirm(
        `Would you like to save "${locationName}" to your favorites?`
      );
      
      if (saveToFavorites) {
        saveFavoriteLocation(locationName, 0, 0, locationType);
      }
    }
  };

  // Location management functions
  const saveFavoriteLocation = (name, lat, lng, type = '📍') => {
    const newFavorite = { name, lat, lng, type, timestamp: Date.now() };
    const updatedFavorites = [...favoriteLocations, newFavorite];
    setFavoriteLocations(updatedFavorites);
    localStorage.setItem('locationFavorites', JSON.stringify(updatedFavorites));
  };

  const removeFavoriteLocation = (index) => {
    const updatedFavorites = favoriteLocations.filter((_, i) => i !== index);
    setFavoriteLocations(updatedFavorites);
    localStorage.setItem('locationFavorites', JSON.stringify(updatedFavorites));
  };

  const addToHistory = (lat, lng, name) => {
    const newHistory = { lat, lng, name, timestamp: Date.now() };
    const updatedHistory = [newHistory, ...locationHistory.filter(h => 
      h.lat !== lat || h.lng !== lng || h.name !== name
    )].slice(0, 10); // Keep only last 10
    setLocationHistory(updatedHistory);
    localStorage.setItem('locationHistory', JSON.stringify(updatedHistory));
  };

  const detectLocationType = (time) => {
    const hour = time.getHours();
    if (hour >= 9 && hour <= 17) return "🏢";
    if (hour >= 18 || hour <= 8) return "🏠";
    return "📍";
  };

  const shareLocation = (lat, lng, name, type = "📍") => {
    const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    const locationMessage = `${type} ${name}\n🌐 ${mapsUrl}`;
    
    // Create a temporary message for immediate UI update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      conversation_id: activeConv.id,
      sender_id: user.id,
      content: locationMessage,
      created_at: new Date().toISOString(),
      status: 'sending'
    };
    
    // Add message to local state immediately
    setMessages(prev => [...prev, tempMessage]);
    
    // Scroll to bottom immediately after adding message
    setTimeout(scrollToBottom, 50);
    
    // Send the message
    sendMessage(locationMessage);
    addToHistory(lat, lng, name);
    setShowLocationModal(false);
  };

  // Message management functions
  const copyMessage = async (messageContent) => {
    try {
      // Remove markdown links and get clean text
      const cleanText = messageContent.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      await navigator.clipboard.writeText(cleanText);
      
      // Show success feedback
      const originalText = document.title;
      document.title = 'Message copied!';
      // Reset title after 1 second using requestAnimationFrame for better security
      const resetTitle = () => { document.title = originalText; };
      const timer = window.setTimeout(resetTitle, 1000);
    } catch (error) {
      logger.error('Failed to copy message:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = messageContent.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const deleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        const { error } = await supabase
          .from('messages')
          .delete()
          .eq('id', messageId);
        
        if (error) {
          logger.error('Error deleting message:', error);
          alert('Failed to delete message. Please try again.');
        } else {
          // Remove message from local state
          setMessages(prev => prev.filter(msg => msg.id !== messageId));
        }
      } catch (error) {
        logger.error('Error deleting message:', error);
        alert('Failed to delete message. Please try again.');
      }
    }
  };

  const handleMessageMenu = (messageId, event) => {
    event.stopPropagation();
    setShowMessageMenu(showMessageMenu === messageId ? null : messageId);
  };

  const closeMessageMenu = () => {
    setShowMessageMenu(null);
  };

  const closeLocationModal = () => {
    setShowLocationModal(false);
    setCurrentLocation(null);
    setLocationError(null);
    setCustomLocationName('');
    setIsLoadingLocation(false);
    setShowFavorites(false);
  };

  // Auto-grow textarea
  const handleInput = (e) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }

    // Note: Typing indicator logic removed - it was showing the indicator when the current user types,
    // which is incorrect. The typing indicator should only show when the OTHER user is typing.
    // To implement real-time typing indicators, you would need to:
    // 1. Send typing status to the other user via Supabase real-time or a separate channel
    // 2. Listen for typing status from the other user
    // 3. Only show the indicator when receiving typing status from the other user
  };

  // Handle Enter/Shift+Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePaste = async (e) => {
    try {
      const clipboardData = await navigator.clipboard.readText();
      if (clipboardData) {
        // Insert clipboard text at cursor position
        const textarea = e.target;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = textarea.value.substring(0, start) + clipboardData + textarea.value.substring(end);
        setInput(newValue);
        
        // Set cursor position after pasted text
        setTimeout(() => {
          textarea.setSelectionRange(start + clipboardData.length, start + clipboardData.length);
          textarea.focus();
        }, 0);
      }
    } catch (error) {
      // Fallback: let browser handle paste normally
      logger.debug('Paste handled by browser');
    }
  };

  // Drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  // Emoji picker removed
  const handleEmoji = () => {};

  // Helper functions for conversation list
  const formatMessagePreview = (content) => {
    if (!content) return isArabic ? 'لا توجد رسائل بعد' : 'No messages yet.';
    if (content.includes('📍')) return '📍 Location shared';
    if (content.includes('🌐')) return '🌐 Location shared';
    if (content.includes('📎')) return '📎 File shared';
    if (content.includes('attachment_url')) return '📎 File shared';
    if (content.length > 50) return content.substring(0, 50) + '...';
    return content;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffHours = (now - messageTime) / (1000 * 60 * 60);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return messageTime.toLocaleDateString();
  };

  // Get message status icon
  const getStatusIcon = (status) => {
    if (!status) return null;
    const colorMap = {
      'sending': 'rgba(255,255,255,0.5)',
      'sent': 'rgba(255,255,255,0.7)',
      'delivered': '#60a5fa',
      'read': '#ff7a1a'
    };
    const iconStyle = {
      fontSize: 13,
      color: colorMap[status] || 'rgba(255,255,255,0.7)',
      marginLeft: 6,
      display: 'inline-flex',
      alignItems: 'center',
      fontWeight: status === 'read' ? 600 : 400,
      transition: 'color 0.2s'
    };
    
    if (status === 'queued') {
      return <FaClock size={13} style={{ ...iconStyle, color: '#f59e0b' }} title="Queued - will send when online" />;
    } else if (status === 'sending') {
      return <FaHourglassHalf size={13} style={iconStyle} />;
    } else if (status === 'sent') {
      return <FaCheck size={13} style={iconStyle} />;
    } else if (status === 'delivered' || status === 'read') {
      return <span style={iconStyle}><FaCheck size={13} /><FaCheck size={13} style={{ marginLeft: -4 }} /></span>;
    }
    return <FaCheck size={13} style={iconStyle} />;
  };
  
  // Format message timestamp
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (isArabic) {
      if (diffMins < 1) return 'الآن';
      if (diffMins < 60) return `منذ ${diffMins} د`;
      if (diffHours < 24) return `منذ ${diffHours} س`;
      if (diffDays === 1) return 'أمس';
      if (diffDays < 7) return `منذ ${diffDays} يوم`;
      return date.toLocaleDateString('ar-BH', { month: 'short', day: 'numeric' });
    } else {
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Filter conversations based on search and filters
  const filteredConversations = conversations.filter(conv => {
    const otherUserId = user && user.id === conv.user1_id ? conv.user2_id : conv.user1_id;
    const otherUser = userDetails[otherUserId] || { name: 'Unknown User' };
    const lastMessage = lastMessages[conv.id];
    const messageContent = lastMessage?.content || '';
    
    // Search filter
    const matchesSearch = searchQuery === '' || 
      otherUser.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      messageContent.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  // Mobile view: Show conversations list or individual chat
  if (isMobile) {
  return (
    <div
        style={{
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden',
          background: '#f7f9fb',
          fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      onClick={closeMessageMenu}
    >
      {/* Connection Status Banner */}
      {showOfflineBanner && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: '#fff',
          padding: '10px 16px',
          textAlign: 'center',
          fontSize: '13px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          <FaExclamationCircle size={16} />
          <span>You're offline. Messages will be sent when connection is restored.</span>
        </div>
      )}

      {/* Mobile Conversations List */}
      {mobileView === 'conversations' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ 
              background: '#fff', 
              padding: '24px 20px 16px 20px', 
              paddingLeft: isArabic ? '20px' : '60px',
              paddingRight: isArabic ? '60px' : '20px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h1 style={{ 
                fontSize: 28, 
                fontWeight: 700, 
                color: '#1E293B', 
                margin: 0,
                marginBottom: 8
              }}>
                {isArabic ? 'الرسائل' : 'Messages'}
              </h1>
              <p style={{
                fontSize: 14,
                color: '#64748B',
                margin: 0
              }}>
                {isArabic ? 'تواصل مع المعلمين والطلاب' : 'Chat with your teachers and students'}
              </p>
            </div>

            {/* Conversations List */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
              {conversations.length === 0 ? (
                <div style={{ 
                  padding: '60px 24px', 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 24,
                    boxShadow: '0 8px 30px rgba(255, 122, 26, 0.3)'
                  }}>
                    <FaComments size={48} color="#fff" />
                  </div>
                  <h3 style={{ 
                    fontSize: 20, 
                    fontWeight: 700, 
                    color: '#111827', 
                    margin: '0 0 8px 0' 
                  }}>
                    {isArabic ? 'لا توجد محادثات بعد' : 'No Conversations Yet'}
                  </h3>
                  <p style={{ 
                    fontSize: 14, 
                    color: '#6b7280', 
                    margin: 0,
                    maxWidth: 280,
                    lineHeight: 1.5
                  }}>
                    {isArabic 
                      ? 'ابدأ محادثة مع معلمك لمناقشة جلساتك القادمة'
                      : 'Start a conversation with your teacher to discuss your upcoming sessions'}
                  </p>
                </div>
              ) : conversations.map((conv) => {
                const otherUserId = user && user.id === conv.user1_id ? conv.user2_id : conv.user1_id;
                const otherUser = userDetails[otherUserId] || { name: 'Unknown User', avatar: null, role: 'student' };
                const lastMessage = lastMessages[conv.id];
                const messagePreview = lastMessage?.content ?
                  (lastMessage.content.length > 50 ? lastMessage.content.substring(0, 50) + '...' : lastMessage.content) :
                  isArabic ? 'لا توجد رسائل بعد' : 'No messages yet';
                const timestamp = lastMessage?.created_at ?
                  new Date(lastMessage.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                const role = otherUser.role || 'student';

                return (
                  <div
                    key={conv.id}
                    onClick={() => handleMobileConversationSelect(conv)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '14px 16px',
                      background: '#fff',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background 0.2s'
                    }}
                  >
                    <img
                      src={otherUser.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(otherUser.name)}
                      alt={otherUser.name}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        background: '#f0f0f0',
                        marginRight: 12,
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Name */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 4
                      }}>
                        <h3 style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: '#111827',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {otherUser.name}
                        </h3>
                      </div>

                      {/* Message preview and timestamp */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8
                      }}>
                        <p style={{
                          fontSize: 13,
                          color: '#6b7280',
                          margin: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {messagePreview}
                        </p>
                        {timestamp && (
                          <span style={{
                            fontSize: 12,
                            color: '#9ca3af',
                            flexShrink: 0
                          }}>
                            {timestamp}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile Chat View */}
        {mobileView === 'chat' && activeConv && (
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100vh',
            maxHeight: '100vh',
            overflow: 'hidden'
          }}>
            {/* Chat Header with Back Button - Fixed at top */}
            <div style={{ 
              background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)', 
              padding: '14px 16px', 
              borderBottom: '2px solid #f3f4f6',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
            }}>
        <button
                onClick={handleMobileBackToConversations}
          style={{
                  background: '#f3f4f6',
            border: 'none',
                  fontSize: 18,
                  color: '#374151',
                  cursor: 'pointer',
                  padding: '8px 10px',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                aria-label={isArabic ? "العودة إلى المحادثات" : "Back to conversations"}
              >
                ←
              </button>
              {(() => {
                const otherUserId = user && user.id === activeConv.user1_id ? activeConv.user2_id : activeConv.user1_id;
                const otherUser = userDetails[otherUserId] || { name: 'Unknown User', avatar: null, role: 'student' };
                const role = otherUser.role || 'student';
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flex: 1,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleOpenProfile(otherUserId)}
                  >
                    <img
                      src={otherUser.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(otherUser.name)}
                      alt={otherUser.name}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        background: '#f0f0f0'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <h2 style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: '#111827',
                        margin: 0
                      }}>
                        {otherUser.name}
                      </h2>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Chat Messages - Mobile */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '12px 16px',
                paddingBottom: '100px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                background: '#fff',
                minHeight: 0,
                maxHeight: 'calc(100vh - 120px)' // Reserve space for header and input
              }}
              onClick={() => setShowMessageMenu(null)}
            >
              {msgLoading ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>{isArabic ? 'جاري تحميل الرسائل...' : 'Loading messages...'}</div>
              ) : messages.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>{isArabic ? 'لا توجد رسائل بعد' : 'No messages yet.'}</div>
              ) : (
                <div>
                  {messages.map((msg) => {
                    const isMe = user && msg.sender_id === user.id;
                    return (
                      <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '100%', position: 'relative' }}>
                          <div
                            style={{
                              background: isMe ? '#232B3E' : '#FF7426',
                              color: '#fff',
                              borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              padding: '8px 12px',
                              fontSize: 14,
                              fontWeight: 500,
                              maxWidth: '80vw',
                              wordWrap: 'break-word',
                              position: 'relative',
                              flex: 1
                            }}
                          >
                            {msg.content}
                            {/* Show attachment if exists */}
                            {msg.attachment_url && (
                              <div style={{ marginTop: 8 }}>
                                {msg.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  // Image attachment - clickable for preview
                                  <img 
                                    src={msg.attachment_url} 
                                    alt={msg.attachment_name || 'Attachment'} 
                                    onClick={() => {
                                      setPreviewImage({
                                        url: msg.attachment_url,
                                        name: msg.attachment_name || 'Image'
                                      });
                                      setShowImagePreview(true);
                                    }}
                                    style={{ 
                                      maxWidth: '100%', 
                                      maxHeight: 200, 
                                      borderRadius: 8,
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      transition: 'opacity 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                  />
                                ) : (
                                  // File attachment - clickable for download
                                  <button
                                    onClick={() => {
                                      // Create download link
                                      const link = document.createElement('a');
                                      link.href = msg.attachment_url;
                                      link.download = msg.attachment_name || 'download';
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    style={{ 
                                      color: '#fff', 
                                      textDecoration: 'underline',
                                      display: 'block',
                                      marginTop: 4,
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: 'inherit',
                                      fontFamily: 'inherit',
                                      padding: 0,
                                      textAlign: 'left',
                                      width: '100%'
                                    }}
                                  >
                                    <FaPaperclip size={14} style={{ marginRight: 4 }} /> {msg.attachment_name || 'Download File'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Three dots menu for mobile */}
                          <div style={{ position: 'relative' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                logger.debug('Three dots clicked for message:', msg.id);
                                setShowMessageMenu(showMessageMenu === msg.id ? null : msg.id);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#9ca3af',
                                fontSize: 16,
                                cursor: 'pointer',
                                padding: 4,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 24,
                                height: 24,
                                zIndex: 10
                              }}
                              aria-label="Message options"
                            >
                              ⋯
                            </button>
                            
                            {/* Message menu dropdown */}
                            {showMessageMenu === msg.id && (
                              <div style={{
                                position: 'absolute',
                                background: '#fff',
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                zIndex: 100,
                                minWidth: 120,
                                top: 30,
                                right: isMe ? 0 : 'auto',
                                left: isMe ? 'auto' : 0,
                                transform: isMe ? 'translateX(0)' : 'translateX(0)'
                              }}>
                            <button
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(msg.content);
                                  alert(isArabic ? 'تم نسخ الرسالة' : 'Message copied to clipboard');
                                } catch (err) {
                                  logger.error('Failed to copy:', err);
                                  alert(isArabic ? 'فشل في نسخ الرسالة' : 'Failed to copy message');
                                }
                                setShowMessageMenu(null);
                              }}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                background: 'none',
                                border: 'none',
                                textAlign: 'left',
                                fontSize: 14,
                                color: '#232B3E',
            cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <FaClipboardList size={14} style={{ marginRight: 4 }} /> {isArabic ? 'نسخ' : 'Copy'}
                            </button>
                            {isMe && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(isArabic ? 'هل تريد حذف هذه الرسالة؟' : 'Are you sure you want to delete this message?')) {
                                    try {
                                      // Delete message from database
                                      const { error } = await supabase
                                        .from('messages')
                                        .delete()
                                        .eq('id', msg.id);
                                      
                                      if (error) {
                                        logger.error('Delete error:', error);
                                        alert(isArabic ? 'فشل في حذف الرسالة' : 'Failed to delete message');
                                      } else {
                                        // Remove from local state
                                        setMessages(prev => prev.filter(m => m.id !== msg.id));
                                      }
                                    } catch (err) {
                                      logger.error('Delete error:', err);
                                      alert(isArabic ? 'فشل في حذف الرسالة' : 'Failed to delete message');
                                    }
                                  }
                                  setShowMessageMenu(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  background: 'none',
                                  border: 'none',
                                  textAlign: 'left',
                                  fontSize: 14,
                                  color: '#dc2626',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8
                                }}
                              >
                                <FaTrash size={14} style={{ marginRight: 4 }} /> {isArabic ? 'حذف' : 'Delete'}
                              </button>
                            )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <span style={{ 
                          fontSize: 10, 
                          color: '#9ca3af', 
                          marginTop: 2,
                          alignSelf: isMe ? 'flex-end' : 'flex-start'
                        }}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    );
                  })}
                  {/* Scroll target for auto-scroll to bottom */}
                  <div ref={messagesEndRef} style={{ height: '20px' }} />
                </div>
              )}
            </div>

            {/* Mobile Input Bar - Fixed at bottom */}
            <form
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#fff',
                borderTop: '1px solid #e5e7eb',
                flexShrink: 0,
                position: 'sticky',
                bottom: 30,
                zIndex: 10,
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
              }}
              onSubmit={(e) => {
                e.preventDefault();
                if (input.trim()) {
                  sendMessage(input.trim());
                  setInput('');
                }
              }}
            >
              {/* Attachment Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  color: uploading ? '#d1d5db' : '#6b7280',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  padding: 8,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => !uploading && (e.target.style.color = '#232B3E')}
                onMouseLeave={(e) => !uploading && (e.target.style.color = '#6b7280')}
                aria-label={uploading ? (isArabic ? "جاري الرفع..." : "Uploading...") : (isArabic ? "إرفاق ملف" : "Attach file")}
              >
                {uploading ? <FaHourglassHalf size={20} /> : <FaPaperclip size={20} />}
              </button>
              
              {/* Location Button */}
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: 8,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#232B3E'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                aria-label={isArabic ? "مشاركة الموقع" : "Share location"}
              >
                <FaMapMarkerAlt size={20} />
              </button>
              
              <input
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isArabic ? 'اكتب رسالة...' : 'Type a message...'}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 20,
                  fontSize: 14,
                  outline: 'none',
                  background: '#f9fafb'
                }}
              />
              <button
                type="submit"
                disabled={!input.trim()}
                style={{
                  background: input.trim() ? '#232B3E' : '#d1d5db',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: input.trim() ? 'pointer' : 'not-allowed',
                  fontSize: 16
                }}
              >
                →
              </button>
            </form>
            
            {/* Hidden file input for mobile */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  // Validate files first
                  const validFiles = [];
                  for (const file of files) {
                    // Check file size (max 10MB)
                    if (file.size > 10 * 1024 * 1024) {
                      alert('File too large. Maximum size is 10MB.');
                      continue;
                    }

                    // Check file type
                    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
                    if (!allowedTypes.includes(file.type)) {
                      alert('File type not supported. Please upload images, PDFs, or text files.');
                      continue;
                    }

                    validFiles.push(file);
                  }

                  if (validFiles.length > 0) {
                    setMobileSelectedFiles(validFiles);
                    setShowMobilePreview(true);
                  }
                  
                  // Reset input
                  e.target.value = '';
                }
              }}
            />
            
            {/* Mobile File Preview Modal */}
            {showMobilePreview && (
              <div style={{
            position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                padding: '20px'
              }}>
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 20
                }}>
                  <h3 style={{ color: '#fff', fontSize: 18, fontWeight: 600, margin: 0 }}>
                    {isArabic ? 'معاينة الملفات' : 'File Preview'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowMobilePreview(false);
                      setMobileSelectedFiles([]);
                    }}
                    style={{
                      background: 'none',
            border: 'none',
                      color: '#fff',
                      fontSize: 24,
                      cursor: 'pointer',
                      padding: 4
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* File Previews */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {mobileSelectedFiles.map((file, index) => (
                    <div key={index} style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 40,
                          height: 40,
            borderRadius: 8,
                          background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
                          fontSize: 20
                        }}>
                          {file.type.startsWith('image/') ? <FaImage size={20} /> : <FaFileAlt size={20} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: 600, color: '#232B3E' }}>
                            {file.name}
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      
                      {/* Image Preview */}
                      {file.type.startsWith('image/') && (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: '100%',
                            maxHeight: 200,
                            objectFit: 'cover',
                            borderRadius: 8
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: 12,
                  marginTop: 20
                }}>
                  <button
                    onClick={() => {
                      setShowMobilePreview(false);
                      setMobileSelectedFiles([]);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button
                    onClick={async () => {
                      setShowMobilePreview(false);
                      setUploading(true);
                      
                      try {
                        for (const file of mobileSelectedFiles) {
                          // Generate unique filename
                          const fileExt = file.name.split('.').pop();
                          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
                          const filePath = `chat-attachments/${activeConv.id}/${fileName}`;

                          // Upload to Supabase Storage
                          const { data, error } = await supabase.storage
                            .from('chat-files')
                            .upload(filePath, file);

                          if (error) {
                            logger.error('Upload error:', error);
                            alert('Failed to upload file. Please try again.');
                            continue;
                          }

                          // Get public URL
                          const { data: { publicUrl } } = supabase.storage
                            .from('chat-files')
                            .getPublicUrl(filePath);

                          // Send message with attachment
                          const attachmentMessage = `📎 ${file.name}`;
                          sendMessage(attachmentMessage, publicUrl, file.name);
                        }
                      } catch (error) {
                        logger.error('Upload error:', error);
                        alert('Failed to upload files. Please try again.');
                      } finally {
                        setUploading(false);
                        setMobileSelectedFiles([]);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#232B3E',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {isArabic ? 'إرسال' : 'Send'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Mobile Image Preview Modal */}
            {showImagePreview && previewImage && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.9)',
                zIndex: 1001,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}>
                {/* Header */}
                <div style={{
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  right: 20,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  zIndex: 1002
                }}>
                  <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 600, margin: 0 }}>
                    {previewImage.name}
                  </h3>
                  <button
                    onClick={() => {
                      setShowImagePreview(false);
                      setPreviewImage(null);
                    }}
                    style={{
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: 'none',
                      color: '#fff',
                      fontSize: 24,
            cursor: 'pointer',
                      padding: 8,
                      borderRadius: 4
          }}
        >
                    ✕
        </button>
                </div>

                {/* Image */}
                <img
                  src={previewImage.url}
                  alt={previewImage.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80%',
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                />

                {/* Download Button */}
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = previewImage.url;
                    link.download = previewImage.name;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  style={{
                    position: 'absolute',
                    bottom: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#232B3E',
                    color: '#fff',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 8,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  📥 {isArabic ? 'تحميل' : 'Download'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Desktop view
  // Calculate negative margins to counteract parent padding from StudentDashboardLayout
  const getResponsiveMargin = () => {
    const width = window.innerWidth;
    if (width >= 1024) return -48; // lg:p-12 (3rem = 48px)
    if (width >= 768) return -40; // md:p-10 (2.5rem = 40px)
    if (width >= 640) return -32; // sm:p-8 (2rem = 32px)
    if (width >= 480) return -24; // xs:p-6 (1.5rem = 24px)
    return -16; // p-4 (1rem = 16px)
  };

  const margin = getResponsiveMargin();

  return (
      <div
        style={{
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        background: '#f7f9fb',
        fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        margin: `${margin}px`,
        width: `calc(100% + ${Math.abs(margin) * 2}px)`
      }}
      onClick={closeMessageMenu}
    >
      {/* Connection Status Banner */}
      {showOfflineBanner && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: '#fff',
          padding: '12px 20px',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          <FaExclamationCircle size={18} />
          <span>You're offline. Messages will be sent when connection is restored.</span>
        </div>
      )}

      {/* Main chat container - full width 2-column layout */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          background: '#fff',
          maxWidth: '100vw',
          flex: 1,
          margin: '0',
          borderRadius: 0,
          overflow: 'hidden',
          alignItems: 'stretch',
          width: '100%',
          minHeight: 0,
        }}
      >
        {/* Chat List - Left Panel */}
        <div
          style={{
            width: 360,
            background: '#fff',
            borderRight: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            flexShrink: 0,
          }}
        >
          {/* Messages Header */}
          <div style={{ 
            padding: '24px 20px 16px', 
            paddingLeft: isArabic ? '20px' : '60px',
            paddingRight: isArabic ? '60px' : '20px',
            borderBottom: '1px solid #e5e7eb' 
          }}>
            <h1 style={{
              fontSize: 28,
              fontWeight: 700,
              color: '#1E293B',
              margin: 0,
              marginBottom: 8
            }}>
              {isArabic ? 'الرسائل' : 'Messages'}
            </h1>
            <p style={{
              fontSize: 14,
              color: '#64748B',
              margin: 0
            }}>
              {isArabic ? 'تواصل مع المعلمين والطلاب' : 'Chat with your teachers and students'}
            </p>
          </div>

          {/* Search Bar */}
          <div style={{ padding: '16px 20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: '#f3f4f6',
              borderRadius: 8,
              padding: '10px 14px',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.background = '#e5e7eb';
            }}
            onBlur={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
            }}
            >
              <FaSearch size={14} color="#6b7280" style={{ marginRight: 10, flexShrink: 0 }} />
              <input
                type="text"
                placeholder={isArabic ? "البحث عن الطلاب أو المعلمين..." : "Search students or teachers..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 14,
                  flex: 1,
                  color: '#111827',
                  fontWeight: 400
                }}
              />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ 
                      background: '#f3f4f6', 
                      border: 'none', 
                      color: '#6b7280', 
                      cursor: 'pointer', 
                      padding: '4px 8px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#ff7a1a';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.color = '#6b7280';
                    }}
                  >
                    {isArabic ? 'مسح' : 'Clear'}
                  </button>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ 
                  padding: '60px 24px', 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <LoadingSpinner size={60} />
                  <span style={{ color: '#6b7280', fontSize: 14, marginTop: 16 }}>
                    {isArabic ? 'جاري تحميل المحادثات...' : 'Loading conversations...'}
                  </span>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div style={{ 
                  padding: '60px 24px', 
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: searchQuery 
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                      : 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                    boxShadow: searchQuery 
                      ? '0 8px 25px rgba(99, 102, 241, 0.3)'
                      : '0 8px 25px rgba(255, 122, 26, 0.3)'
                  }}>
                    {searchQuery ? <FaSearch size={36} color="#fff" /> : <FaComments size={36} color="#fff" />}
                  </div>
                  <h3 style={{ 
                    fontSize: 18, 
                    fontWeight: 700, 
                    color: '#111827', 
                    margin: '0 0 8px 0' 
                  }}>
                    {searchQuery 
                      ? (isArabic ? 'لا توجد نتائج' : 'No Results Found')
                      : (isArabic ? 'لا توجد محادثات' : 'No Conversations')}
                  </h3>
                  <p style={{ 
                    fontSize: 14, 
                    color: '#6b7280', 
                    margin: 0,
                    maxWidth: 220,
                    lineHeight: 1.5
                  }}>
                    {searchQuery 
                      ? (isArabic ? 'جرب البحث بكلمات مختلفة' : 'Try searching with different words')
                      : (isArabic ? 'ابدأ محادثة مع معلمك' : 'Start a chat with your teacher')}
                  </p>
                </div>
              ) : filteredConversations.map((conv, idx) => {
                const otherUserId = user && user.id === conv.user1_id ? conv.user2_id : conv.user1_id;
                const otherUser = userDetails[otherUserId] || { name: 'Unknown User', avatar: null, role: 'student' };
                const lastMessage = lastMessages[conv.id];
                const messagePreview = formatMessagePreview(lastMessage?.content);
                const timestamp = formatTimestamp(lastMessage?.created_at);
                const isTypingInConv = typingUsers[conv.id] || false;
                const role = otherUser.role || 'student';

                return (
                  <div
                    key={conv.id}
                    onClick={() => setActiveConv(conv)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 20px',
                      background: activeConv && activeConv.id === conv.id ? '#f3f4f6' : '#fff',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!activeConv || activeConv.id !== conv.id) {
                        e.currentTarget.style.background = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!activeConv || activeConv.id !== conv.id) {
                        e.currentTarget.style.background = '#fff';
                      }
                    }}
                  >
                    {/* Avatar */}
                    <img
                      src={otherUser.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(otherUser.name)}
                      alt={otherUser.name}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        background: '#f0f0f0',
                        flexShrink: 0
                      }}
                    />

                    {/* Contact details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Name */}
                      <div style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: 4
                      }}>
                        {otherUser.name}
                      </div>

                      {/* Message preview and timestamp */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8
                      }}>
                        <div style={{
                          color: isTypingInConv ? '#3b82f6' : '#6b7280',
                          fontSize: 13,
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1
                        }}>
                          {isTypingInConv ? (
                            <span style={{ fontStyle: 'italic' }}>
                              {isArabic ? 'يكتب...' : 'typing...'}
                            </span>
                          ) : (
                            messagePreview
                          )}
                        </div>
                        {timestamp && (
                          <span style={{
                            fontSize: 12,
                            color: '#9ca3af',
                            flexShrink: 0
                          }}>
                            {timestamp}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        {/* Chat Area */}
        <div
          style={{
      flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? '100vh' : '100%',
            maxHeight: isMobile ? '100vh' : '100%',
            background: '#fff',
            minWidth: 0,
            borderRadius: isMobile ? 0 : 0,
            overflow: 'hidden',
            borderLeft: isMobile ? 'none' : '1.5px solid #f0f0f0',
          }}
        >
          {/* Chat Header */}
          <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1.5px solid #f0f0f0', background: '#fff' }}>
            {/* Platform Reminder Banner */}
            {activeConv && (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                padding: '8px 16px',
                borderBottom: '1px solid #fcd34d',
                fontSize: 11,
                color: '#92400e',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 600
              }}>
                <FaLock size={16} />
                <span>
                  {isArabic 
                    ? 'جميع الحجوزات يجب أن تتم عبر المنصة للحصول على الحماية والميزات الحصرية'
                    : 'All bookings must be made through the platform for protection and exclusive features'}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px' }}>
              {activeConv ? (() => {
                const otherUserId = user && user.id === activeConv.user1_id ? activeConv.user2_id : activeConv.user1_id;
                const otherUser = userDetails[otherUserId] || { name: 'Unknown User', avatar: null, role: 'student' };
                const role = otherUser.role || 'student';

                return (
                  <>
                    {/* Left side: Profile picture + name + role */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                      onClick={() => handleOpenProfile(otherUserId)}
                    >
                      <img
                        src={otherUser.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(otherUser.name)}
                        alt={otherUser.name}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          background: '#f0f0f0'
                        }}
                      />
                      <div>
                        <span style={{
                          fontWeight: 600,
                          fontSize: 16,
                          color: '#111827'
                        }}>
                          {otherUser.name}
                        </span>
                      </div>
                    </div>

                    {/* Right side: Info icon */}
                    <button
                      onClick={() => handleOpenProfile(otherUserId)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 8,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                    >
                      <FaInfoCircle size={20} color="#6b7280" />
                    </button>
                  </>
                );
              })() : (
                <div style={{ fontSize: 16, fontWeight: 600, color: '#6b7280' }}>
                  {isArabic ? 'اختر محادثة' : 'Select a conversation'}
                </div>
              )}
            </div>
          </div>
          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: isMobile ? '12px 4px' : '32px 32px',
            paddingBottom: isMobile ? '120px' : '32px',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 7 : 12,
            background: '#fff',
            minHeight: 0
          }}>
            {msgLoading ? (
              <div style={{ 
                padding: '60px 24px', 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{
                  width: 40,
                  height: 40,
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#ff7a1a',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  marginBottom: 12
                }} />
                <span style={{ color: '#6b7280', fontSize: 14 }}>
                  {isArabic ? 'جاري تحميل الرسائل...' : 'Loading messages...'}
                </span>
              </div>
            ) : messages.length === 0 ? (
              <div style={{ 
                padding: '60px 24px', 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16
                }}>
                  <span style={{ fontSize: 32 }}>👋</span>
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 8px 0' }}>
                  {isArabic ? 'ابدأ المحادثة!' : 'Start the conversation!'}
                </h3>
                <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
                  {isArabic ? 'أرسل رسالة للتواصل مع معلمك' : 'Send a message to connect with your teacher'}
                </p>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => {
              const isMe = user && msg.sender_id === user.id;
              
              // Date separator logic
              const msgDate = new Date(msg.created_at);
              const prevMsg = index > 0 ? messages[index - 1] : null;
              const prevMsgDate = prevMsg ? new Date(prevMsg.created_at) : null;
              const showDateSeparator = !prevMsgDate || 
                msgDate.toDateString() !== prevMsgDate.toDateString();
              
              const getDateLabel = (date) => {
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                
                if (date.toDateString() === today.toDateString()) {
                  return isArabic ? 'اليوم' : 'Today';
                } else if (date.toDateString() === yesterday.toDateString()) {
                  return isArabic ? 'أمس' : 'Yesterday';
                } else {
                  return date.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  });
                }
              };
              
              return (
                <React.Fragment key={msg.id}>
                  {/* Date Separator */}
                  {showDateSeparator && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '20px 0 16px 0'
                    }}>
                      <div style={{
                        flex: 1,
                        height: 1,
                        background: 'linear-gradient(90deg, transparent, #FF7426, transparent)'
                      }} />
                      <span style={{
                        padding: '6px 16px',
                        background: '#fff7ed',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#FF7426',
                        margin: '0 16px',
                        border: '1px solid #fed7aa'
                      }}>
                        {getDateLabel(msgDate)}
                      </span>
                      <div style={{
                        flex: 1,
                        height: 1,
                        background: 'linear-gradient(90deg, transparent, #FF7426, transparent)'
                      }} />
                    </div>
                  )}
                  
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                  <div
                    style={{
                      background: isMe
                        ? '#2b3d5c'
                        : '#f0f0f0',
                      color: isMe ? '#fff' : '#1f2937',
                      borderRadius: 14,
                      padding: '12px 16px',
                      fontSize: 14,
                      fontWeight: 400,
                      maxWidth: '70%',
                      boxShadow: isMe
                        ? '0 1px 2px rgba(0,0,0,0.1)'
                        : '0 1px 2px rgba(0,0,0,0.05)',
                      wordBreak: 'break-word',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      lineHeight: 1.5
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {msg.attachment_url && (
                          <div style={{ marginBottom: 8 }}>
                            {msg.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                              <img 
                                src={msg.attachment_url} 
                                alt="Attachment" 
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: 200, 
                                  borderRadius: 8, 
                                  cursor: 'pointer',
                                  border: '2px solid rgba(255,255,255,0.2)'
                                }}
                                onClick={() => window.open(msg.attachment_url, '_blank')}
                              />
                            ) : (
                              <a 
                                href={msg.attachment_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 8, 
                                  padding: '8px 12px', 
                                  background: 'rgba(255,255,255,0.1)', 
                                  borderRadius: 8, 
                                  textDecoration: 'none', 
                                  color: '#fff',
                                  fontSize: 14,
                                  border: '1px solid rgba(255,255,255,0.2)'
                                }}
                              >
                                <FaPaperclip size={14} style={{ marginRight: 4 }} /> {msg.attachment_name || 'File'}
                              </a>
                            )}
                          </div>
                        )}
                        {msg.content && (
                          <span style={{ 
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: isMobile ? 14 : 15,
                            lineHeight: 1.6
                          }}>
                            {msg.content.split('\n').map((line, index) => {
                              // Check if line contains a Google Maps URL
                              if (line.includes('maps.google.com') || line.includes('🌐')) {
                                const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
                                if (urlMatch) {
                                  return (
                                    <div key={index} style={{ marginBottom: index < msg.content.split('\n').length - 1 ? 4 : 0 }}>
                                      <span>{line.replace(urlMatch[0], '')}</span>
                                      <a 
                                        href={urlMatch[0]} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ 
                                          color: '#fff', 
                                          textDecoration: 'underline',
                                          fontWeight: 600,
                                          marginLeft: 4
                                        }}
                                      >
                                        View on Maps
                                      </a>
                                    </div>
                                  );
                                }
                              }
                              return (
                                <div key={index} style={{ marginBottom: index < msg.content.split('\n').length - 1 ? 4 : 0 }}>
                                  {line}
                                </div>
                              );
                            })}
                          </span>
                        )}
                      </div>
                      
                      {/* Message Menu */}
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => handleMessageMenu(msg.id, e)}
                          style={{
                            background: isMe ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                            border: 'none',
                            color: isMe ? 'rgba(255,255,255,0.8)' : '#6b7280',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 32,
                            height: 32
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = isMe ? 'rgba(255,255,255,1)' : '#374151';
                            e.currentTarget.style.background = isMe ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = isMe ? 'rgba(255,255,255,0.8)' : '#6b7280';
                            e.currentTarget.style.background = isMe ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <FaEllipsisH size={14} />
                        </button>
                        
                        {/* Message Menu Dropdown */}
                        {showMessageMenu === msg.id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 8px)',
                              right: 0,
                              background: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '12px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)',
                              zIndex: 1000,
                              minWidth: '140px',
                              padding: '6px 0',
                              animation: 'fadeIn 0.2s ease-out'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                copyMessage(msg.content);
                                closeMessageMenu();
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'none',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#374151',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f9fafb';
                                e.currentTarget.style.color = '#111827';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.color = '#374151';
                              }}
                            >
                              <FaCopy size={14} />
                              {isArabic ? 'نسخ' : 'Copy'}
                            </button>
                            <div style={{
                              height: 1,
                              background: '#e5e7eb',
                              margin: '4px 0'
                            }} />
                            <button
                              type="button"
                              onClick={() => {
                                deleteMessage(msg.id);
                                closeMessageMenu();
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'none',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#dc2626',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#fef2f2';
                                e.currentTarget.style.color = '#b91c1c';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.color = '#dc2626';
                              }}
                            >
                              <FaTrash size={14} />
                              {isArabic ? 'حذف' : 'Delete'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Timestamp and status */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 6, 
                      marginTop: 6,
                      paddingLeft: isMe ? 0 : 0,
                      paddingRight: isMe ? 0 : 0,
                      justifyContent: isMe ? 'flex-end' : 'flex-start',
                      opacity: 0.85
                    }}
                    title={new Date(msg.created_at).toLocaleString(isArabic ? 'ar-BH' : 'en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                    >
                      <span style={{
                        color: isMe ? 'rgba(255,255,255,0.75)' : '#6b7280',
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: '0.3px'
                      }}>
                        {formatMessageTime(msg.created_at)}
                      </span>
                      {isMe && getStatusIcon(messageStatuses[msg.id] || msg.status || 'sent')}
                    </div>
                  </div>
                </div>
                </React.Fragment>
              );
            })}
            {/* Scroll target for auto-scroll to bottom */}
            <div ref={messagesEndRef} />
            {isTyping && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                margin: '12px 0 0 8px',
                padding: '14px 20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '22px 22px 22px 6px',
                maxWidth: 'fit-content',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ 
                  display: 'flex', 
                  gap: 5,
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#ff7a1a',
                    animation: 'typingDot 1.4s infinite ease-in-out',
                    animationDelay: '0s'
                  }}></div>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#ff7a1a',
                    animation: 'typingDot 1.4s infinite ease-in-out',
                    animationDelay: '0.2s'
                  }}></div>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#ff7a1a',
                    animation: 'typingDot 1.4s infinite ease-in-out',
                    animationDelay: '0.4s'
                  }}></div>
                </div>
                <span style={{ color: '#ff7a1a', fontSize: 13, fontWeight: 600 }}>
                  {isArabic ? 'يكتب...' : 'typing...'}
                </span>
              </div>
            )}
              </>
            )}
          </div>
          {/* Input Bar */}
          <form
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 6 : 12,
              padding: isMobile ? '8px 8px' : '18px 32px',
              background: dragActive ? '#fffbe9' : '#fff',
              borderTop: '1.5px solid #f0f0f0',
              position: isMobile ? 'fixed' : 'relative',
              bottom: isMobile ? '12px' : 'auto',
              left: isMobile ? 0 : 'auto',
              right: isMobile ? 0 : 'auto',
              zIndex: isMobile ? 1000 : 'auto',
              boxShadow: isMobile ? '0 -2px 10px rgba(0,0,0,0.1)' : 'none',
              flexShrink: 0,
              minHeight: 'fit-content'
            }}
            onSubmit={e => { e.preventDefault(); }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Selected Files Display with Progress */}
            {selectedFiles.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8, minHeight: 'fit-content' }}>
                {selectedFiles.map((file, index) => {
                  const progress = uploadProgress[file.name] || 0;
                  const isUploading = uploading && progress > 0 && progress < 100;
                  
                  return (
                    <div 
                      key={index}
                      style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 6,
                        padding: '10px 12px',
                        background: isUploading ? '#f0f9ff' : '#f3f4f6',
                        borderRadius: 10,
                        border: `1px solid ${isUploading ? '#3b82f6' : '#e5e7eb'}`,
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaPaperclip size={18} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, color: '#374151', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </div>
                          {file.size && (
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          )}
                        </div>
                        {!isUploading && (
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#dc2626',
                              cursor: 'pointer',
                              fontSize: 18,
                              padding: 4,
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 4,
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#fee2e2'}
                            onMouseLeave={(e) => e.target.style.background = 'none'}
                          >
                            ×
                          </button>
                        )}
                        {isUploading && (
                          <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                            {progress}%
                          </div>
                        )}
                      </div>
                      {isUploading && (
                        <div style={{
                          width: '100%',
                          height: 4,
                          background: '#e5e7eb',
                          borderRadius: 2,
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${progress}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                            borderRadius: 2,
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12 }}>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: uploading ? '#ccc' : '#FF7426', fontSize: isMobile ? 18 : 22, cursor: uploading ? 'not-allowed' : 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title={isArabic ? "إرفاق ملف" : "Attach file"}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <FaPaperclip />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.txt"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInput}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                    if (input.trim() || selectedFiles.length > 0) {
                      handleSendMessage();
                  }
                } else {
                  handleKeyDown(e);
                }
              }}
                onPaste={handlePaste}
                placeholder={isArabic ? 'اكتب رسالة (Ctrl+V للصق)' : 'Type a message (Ctrl+V to paste)'}
              aria-label={isArabic ? 'اكتب رسالة' : 'Type a message'}
              style={{ flex: 1, padding: isMobile ? '8px 12px' : '12px 18px', borderRadius: 16, border: '1.5px solid #e0eaff', fontSize: isMobile ? 13 : 16, background: '#f7f9fb', color: undefined, outline: 'none', transition: 'border 0.2s', minHeight: isMobile ? 32 : 40, resize: 'none', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto' }}
              rows={1}
            />
              <button
                type="button"
                onClick={handleSendMessage}
                style={{
                  background: '#ff7a1a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 16px',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#ff8c3a'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#ff7a1a'}
              >
                <FaPaperPlane />
              </button>
            </div>
            {/* Emoji picker removed */}
            {dragActive && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,247,227,0.7)', border: '2px dashed #FF7426', borderRadius: 16, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#FF7426', pointerEvents: 'none' }}>
                Drop files to upload
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="modal-overlay" onClick={closeLocationModal}>
          <div className="location-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Location</h3>
              <button className="close-btn" onClick={closeLocationModal}>×</button>
    </div>
            
            <div className="modal-content">
              {isLoadingLocation && (
                <div className="loading-location">
                  <div className="spinner"></div>
                  <p>Getting your current location...</p>
                </div>
              )}

              {locationError && (
                <div className="location-error">
                  <p><FaTimesCircle size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Unable to get your location</p>
                  <p className="error-details">
                    {locationError.code === 1 && "Location access denied. Please allow location access in your browser settings."}
                    {locationError.code === 2 && "Location unavailable. Please check if location services are enabled on your device."}
                    {locationError.code === 3 && "Location request timed out. Please try again."}
                    {locationError.code === 0 && locationError.message}
                  </p>
                  <div className="error-suggestions">
                    <p><strong>Try these solutions:</strong></p>
                    <ul>
                      <li>Enable location services on your device</li>
                      <li>Allow location access in your browser</li>
                      <li>Try using the custom location option below</li>
                      <li>Move to an area with better GPS signal</li>
                    </ul>
                  </div>
                </div>
              )}

              {currentLocation && (
                <div className="current-location-section">
                  <div className="map-container">
                    <div className="map-header">
                      <h4><FaMapMarkerAlt size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Your Current Location</h4>
                      <span className="accuracy-badge"><FaChartBar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {Math.round(currentLocation.accuracy)}m accuracy</span>
                    </div>
                    <div className="map-content">
                      <iframe
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentLocation.longitude-0.01},${currentLocation.latitude-0.01},${currentLocation.longitude+0.01},${currentLocation.latitude+0.01}&layer=mapnik&marker=${currentLocation.latitude},${currentLocation.longitude}`}
                        width="100%"
                        height="200"
                        style={{ border: 0, borderRadius: '8px' }}
                        allowFullScreen=""
                        loading="lazy"
                      />
                    </div>
                    <div className="map-actions">
                      <button 
                        className="open-maps-btn"
                        onClick={() => window.open(`https://maps.google.com/?q=${currentLocation.latitude},${currentLocation.longitude}`, '_blank')}
                      >
                        🌍 Open in Google Maps
                      </button>
                    </div>
                  </div>


                  <button 
                    className="send-location-btn primary"
                    onClick={handleSendCurrentLocation}
                  >
                    <FaMapMarkerAlt size={20} /> Send Current Location
                  </button>
                </div>
              )}

              {/* Quick Share Buttons */}
              <div className="quick-share-section">
                <h4>⚡ Quick Share</h4>
                <div className="quick-share-buttons">
                  <button 
                    className="quick-share-btn current"
                    onClick={() => {
                      if (currentLocation) {
                        handleSendCurrentLocation();
                      }
                    }}
                    disabled={!currentLocation}
                  >
                    <FaMapMarkerAlt size={20} /> Current Location
                  </button>
                  <button 
                    className="quick-share-btn"
                    onClick={() => setShowFavorites(!showFavorites)}
                  >
                    <FaStar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Favorites ({favoriteLocations.length})
                  </button>
                  <button 
                    className="quick-share-btn"
                    onClick={() => {
                      if (locationHistory.length > 0) {
                        const recent = locationHistory[0];
                        shareLocation(recent.lat, recent.lng, recent.name, "🕒");
                      }
                    }}
                    disabled={locationHistory.length === 0}
                  >
                    🕒 Recent ({locationHistory.length})
                  </button>
                </div>
              </div>

              {/* Favorites Section */}
              {showFavorites && (
                <div className="favorites-section">
                  <h4><FaStar size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Favorite Locations</h4>
                  {favoriteLocations.length === 0 ? (
                    <p className="no-favorites">No favorite locations yet. Share a location to save it!</p>
                  ) : (
                    <div className="favorites-list">
                      {favoriteLocations.map((fav, index) => (
                        <div key={index} className="favorite-item">
                          <div className="favorite-info">
                            <span className="favorite-icon">{fav.type}</span>
                            <span className="favorite-name">{fav.name}</span>
                          </div>
                          <div className="favorite-actions">
                            <button 
                              className="share-favorite-btn"
                              onClick={() => shareLocation(fav.lat, fav.lng, fav.name, fav.type)}
                            >
                              <FaMapMarkerAlt size={20} /> Share
                            </button>
                            <button 
                              className="remove-favorite-btn"
                              onClick={() => removeFavoriteLocation(index)}
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Custom Location Section */}
              <div className="custom-location-section">
                <h4>🏢 Or Share Custom Location</h4>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Enter place name (e.g., Home, Office, Mall)"
                    value={customLocationName}
                    onChange={(e) => setCustomLocationName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendCustomLocation()}
                  />
                  <button 
                    className="send-custom-btn"
                    onClick={handleSendCustomLocation}
                    disabled={!customLocationName.trim()}
                  >
                    <FaMapMarkerAlt size={20} /> Send
                  </button>
                </div>
                

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: isMobile ? '20px' : '40px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowProfileModal(false);
            setProfileData(null);
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 24,
              maxWidth: isMobile ? '100%' : 500,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative',
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowProfileModal(false);
                setProfileData(null);
              }}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 20,
                color: '#6b7280',
                zIndex: 1,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#ff7a1a';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              ✕
            </button>

            {loadingProfile ? (
              <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                <div style={{
                  width: 50,
                  height: 50,
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#ff7a1a',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 16px'
                }} />
                <p style={{ color: '#6b7280', fontSize: 14 }}>
                  {isArabic ? 'جاري تحميل الملف الشخصي...' : 'Loading profile...'}
                </p>
              </div>
            ) : profileData ? (
              <>
                {/* Header with gradient background */}
                <div style={{
                  background: 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)',
                  padding: '40px 32px 60px',
                  borderRadius: '24px 24px 0 0',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Decorative circles */}
                  <div style={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)'
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: -30,
                    left: -30,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)'
                  }} />
                  
                  {/* Profile Photo */}
                  <div style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={profileData.profile_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.full_name || 'User')}&size=200&background=fff&color=ff7a1a`}
                      alt={profileData.full_name}
                      style={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '5px solid #fff',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                        marginBottom: 16,
                        display: 'block',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                      }}
                    />
                    {/* Role Badge */}
                    <div style={{
                      display: 'inline-block',
                      background: '#fff',
                      color: '#ff7a1a',
                      padding: '6px 16px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      {profileData.role === 'teacher' 
                        ? (isArabic ? <><FaChalkboardTeacher size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> معلم</> : <><FaChalkboardTeacher size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Teacher</>)
                        : (isArabic ? <><FaUserGraduate size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> طالب</> : <><FaUserGraduate size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Student</>)
                      }
                    </div>
                  </div>
                </div>

                {/* Profile Content */}
                <div style={{ padding: '32px' }}>
                  {/* Name */}
                  <h2 style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: '#111827',
                    margin: '0 0 8px 0',
                    textAlign: 'center'
                  }}>
                    {profileData.full_name}
                  </h2>

                  {/* Teacher-specific fields */}
                  {profileData.role === 'teacher' && (
                    <>
                      {profileData.subjects && (
                        <div style={{ marginBottom: 20 }}>
                          <h3 style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#374151',
                            margin: '0 0 12px 0',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {isArabic ? 'المواد' : 'Subjects'}
                          </h3>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 8
                          }}>
                            {(Array.isArray(profileData.subjects) ? profileData.subjects : [profileData.subjects]).map((subject, idx) => (
                              <span
                                key={idx}
                                style={{
                                  background: 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)',
                                  color: '#fff',
                                  padding: '6px 14px',
                                  borderRadius: 20,
                                  fontSize: 13,
                                  fontWeight: 600
                                }}
                              >
                                {subject}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {profileData.hourly_rate && (
                        <div style={{
                          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                          padding: '16px',
                          borderRadius: 16,
                          marginBottom: 20,
                          border: '1px solid #bae6fd'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}>
                            <span style={{ fontSize: 14, color: '#0369a1', fontWeight: 600 }}>
                              {isArabic ? 'السعر بالساعة' : 'Hourly Rate'}
                            </span>
                            <span style={{ fontSize: 24, fontWeight: 800, color: '#0369a1' }}>
                              {profileData.hourly_rate} BD
                            </span>
                          </div>
                        </div>
                      )}

                      {profileData.bio && (
                        <div style={{ marginBottom: 20 }}>
                          <h3 style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#374151',
                            margin: '0 0 12px 0',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {isArabic ? 'نبذة' : 'Bio'}
                          </h3>
                          <p style={{
                            color: '#6b7280',
                            fontSize: 14,
                            lineHeight: 1.6,
                            margin: 0
                          }}>
                            {profileData.bio}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Student-specific fields */}
                  {profileData.role === 'student' && (
                    <>
                      {profileData.grade && (
                        <div style={{
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                          padding: '16px',
                          borderRadius: 16,
                          marginBottom: 20,
                          border: '1px solid #bbf7d0'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}>
                            <span style={{ fontSize: 14, color: '#166534', fontWeight: 600 }}>
                              {isArabic ? 'الصف' : 'Grade'}
                            </span>
                            <span style={{ fontSize: 20, fontWeight: 800, color: '#166534' }}>
                              {profileData.grade}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <FaUser size={36} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 8px 0' }}>
                  {isArabic ? 'المستخدم غير موجود' : 'User Not Found'}
                </h3>
                <p style={{ color: '#6b7280', fontSize: 14 }}>
                  {isArabic ? 'لا يمكن العثور على معلومات الملف الشخصي' : 'Profile information could not be found'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage; 